import * as Sentry from '@sentry/react'

import { extractCookiesData } from '../helpers/cookieHelpers'

import { isExternalNoise } from './externalNoiseFilters'

export function initSentry() {
    const { statisticAllowed } = extractCookiesData()

    console.log('[Sentry] Cookies extracted:', { statisticAllowed })

    if (!statisticAllowed) return

    Sentry.init({
        dsn: 'https://a4abf369a456074fb70f84081165999e@o4506197688647680.ingest.us.sentry.io/4506707730825216',
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
            Sentry.globalHandlersIntegration({
                onerror: true,
                onunhandledrejection: false,
            }),
        ],
        tracesSampleRate: 1.0,
        tracePropagationTargets: [/.*/],
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        environment: process.env.ENV,
        ignoreErrors: [
            "Failed to read a named property 'Element' from 'Window'",
            'Failed to read a named property',
            'Blocked a frame with origin',
            "Can't find variable: SCDynimacBridge",
            'SCDynimacBridge',
            'setContactAutofillValuesFromBridge',
            'jQuery is not defined',
            '$ is not defined',
            'Error invoking enableDidUserTypeOnKeyboardLogging',
            'Java object is gone',
            'navigation_performance_logger_android',
            'sendBeforeUnloadMessage',
            'window.webkit.messageHandlers',
            "undefined is not an object (evaluating 'window.webkit.messageHandlers')",
            'window.webkit.messageHandlers',
            "undefined is not an object (evaluating 'window.webkit.messageHandlers')",
            'sendDataToNative',
            'sendPageHideMessage',
            'RangeError',
            'Maximum call stack size exceeded',
            'Uncompressed Asset',
            'uncompressed assets',
        ],
        beforeSend: (event) => {
            if (process.env.ENV === 'local') return null
            if (isExternalNoise(event)) return null

            const exceptionValue = event.exception?.values?.[0]?.value || ''
            const exceptionType = event.exception?.values?.[0]?.type || ''
            const browserName = typeof event.contexts?.browser?.name === 'string' ? event.contexts.browser.name : ''
            const eventMessage = event.message || ''
            const logEntry = event.logentry?.message || ''

            const combinedText = `${exceptionValue} ${exceptionType} ${eventMessage} ${logEntry}`.toLowerCase()

            if (
                event.message?.includes('Uncompressed Asset') ||
                (event.level === 'info' &&
                    event.spans?.some(
                        (span) =>
                            span?.description?.includes('.js') &&
                            span?.data?.transferSize === span?.data?.encodedBodySize
                    ))
            ) {
                console.log('[Sentry] Filtered Uncompressed Asset warning')
                return null
            }

            if (event.message?.includes('Uncompressed Asset')) {
                console.log('[Sentry] Filtered out Uncompressed Asset warning')
                return null
            }

            if (event.contexts?.trace?.data?.explanation?.includes('uncompressed')) {
                return null
            }

            if (
                combinedText.includes('rangeerror') ||
                combinedText.includes('maximum call stack size exceeded') ||
                combinedText.includes('call stack')
            ) {
                console.log('[Sentry] Filtered RangeError (combined check)')
                return null
            }

            if (
                exceptionValue.includes('RangeError') ||
                exceptionValue.includes('Maximum call stack size exceeded') ||
                eventMessage.includes('RangeError') ||
                eventMessage.includes('Maximum call stack size exceeded') ||
                exceptionType === 'RangeError'
            ) {
                console.log('[Sentry] Filtered RangeError')
                return null
            }

            if (exceptionValue?.includes('Timeout')) return null
            if (exceptionValue.includes("Cannot read properties of undefined (reading 'value')")) {
                if (browserName === 'Facebook' || browserName?.includes('Facebook')) {
                    console.log('[Sentry] Filtered Facebook autofill error')
                    return null
                }

                if (exceptionValue.includes('setContactAutofillValuesFromBridge')) {
                    console.log('[Sentry] Filtered setContactAutofillValuesFromBridge error')
                    return null
                }
            }
            if (
                exceptionValue.includes('enableDidUserTypeOnKeyboardLogging') ||
                exceptionValue.includes('Java object is gone')
            ) {
                console.log('[Sentry] Filtered Facebook navigation performance error')
                return null
            }

            if (exceptionValue.includes('setContactAutofillValuesFromBridge')) {
                return null
            }
            if (exceptionValue?.includes("Failed to read a named property 'Element' from 'Window'")) {
                return null
            }
            if (exceptionValue.includes('SCDynimacBridge')) {
                return null
            }
            if (exceptionType === 'SecurityError' && exceptionValue.includes('Failed to read a named property')) {
                return null
            }
            if (event.message?.includes('Non-Error promise rejection captured with value: Timeout')) return null
            if (event.message?.includes('Large Render Blocking Asset')) return null
            if (exceptionValue?.includes('RangeError') || exceptionValue?.includes('Maximum call stack size exceeded'))
                return null
            if (exceptionValue?.includes('SecurityError') && exceptionValue?.includes('cross-origin frame')) {
                return null
            }
            if (event.message?.includes('RangeError')) return null
            if (event.message?.includes('Uncompressed Asset')) return null
            if (event.tags?.level === 'info' && event.contexts?.trace?.op === 'pageload' && event.measurements) {
                const hasRenderBlockingResource = event.spans?.some(
                    (span) =>
                        span?.op?.includes('resource') && (span?.endTimestamp ?? 0) - (span?.startTimestamp ?? 0) > 2000
                )

                if (hasRenderBlockingResource) {
                    console.log('[Sentry] Filtered out render blocking asset event for:', event.transaction)
                    return null
                }

                if (event.measurements.fcp_delay?.value > 2000) {
                    console.log('[Sentry] Filtered out event with large FCP delay for:', event.transaction)
                    return null
                }
            }

            return event
        },
        beforeSendTransaction: (transaction) => {
            const longSpans = transaction.spans?.filter(
                (span) =>
                    span?.op?.includes('resource') && (span?.endTimestamp ?? 0) - (span?.startTimestamp ?? 0) > 2000 // > 2 секунди
            )

            if (longSpans && longSpans.length > 0) {
                console.log('[Sentry] Filtered out transaction with long resource loading:', transaction.transaction)
                return null
            }

            return transaction
        },
    })
}
